import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { BsViewList } from 'react-icons/bs';
import { IoCalendarOutline } from 'react-icons/io5';
import { MdOutlineCalendarViewDay, MdOutlineViewWeek } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { setCalendarViewMode } from '../../../redux/ducks/StorageDucks';
import { CalendarViewType } from '../../../redux/ducks/StorageDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';

const CalendarPopupMenu = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { calendarViewMode } = useSelector(
    (state: RootReducerType) => state.storage,
  );

  return (
    <span className="flex min-h-full grow flex-row items-center justify-end px-1 lg:justify-start">
      <Popover className="relative z-20">
        <PopoverButton
          className="flex min-h-full cursor-pointer items-center justify-end gap-1 rounded-full p-2 hover:bg-slate-500 hover:text-white"
          id="calendar-modes-popover-menu-btn"
          key="calendar-modes-popover-menu-btn"
          aria-label="calendar-modes-popover-menu-btn"
          data-test-id="calendar-modes-popover-menu-btn"
          data-umami-event="calendar-modes-popover-menu-btn-click"
          type="button"
        >
          <IoCalendarOutline size={24} />
          {calendarViewMode === CalendarViewType.MonthView ? (
            <span className="hidden font-semibold lg:block">
              {t(
                'static.dashboard.appointments_section.month_view_popup_label',
              )}
            </span>
          ) : calendarViewMode === CalendarViewType.WeekView ? (
            <span className="hidden font-semibold lg:block">
              {t('static.dashboard.appointments_section.week_view_popup_label')}
            </span>
          ) : calendarViewMode === CalendarViewType.DayView ? (
            <span className="hidden font-semibold lg:block">
              {t('static.dashboard.appointments_section.day_view_popup_label')}
            </span>
          ) : (
            <span className="hidden font-semibold lg:block">
              {t('static.dashboard.appointments_section.list_view_popup_label')}
            </span>
          )}
        </PopoverButton>

        <PopoverPanel className="absolute right-0 top-12 z-30 flex w-[200px] flex-col divide-y divide-slate-200 overflow-hidden rounded-xl border border-solid border-slate-200 bg-white text-sm shadow-md lg:left-0">
          {calendarViewMode !== CalendarViewType.MonthView && (
            <PopoverButton
              type="button"
              data-test-id="calendar-select-month-view"
              id="calendar-select-month-view"
              key="calendar-select-month-view"
              aria-label="calendar-select-month-view"
              className="flex min-w-full cursor-pointer flex-row gap-1 p-1 py-3 hover:bg-slate-500 hover:text-white"
              onClick={() => {
                dispatch(setCalendarViewMode(CalendarViewType.MonthView));
              }}
            >
              <span className="flex flex-col items-center justify-center rounded-full p-1">
                <IoCalendarOutline size={18} />
              </span>
              <span className="flex grow flex-col items-start justify-center text-start text-base font-bold">
                {t(
                  'static.dashboard.appointments_section.month_view_popup_label',
                )}
              </span>
            </PopoverButton>
          )}

          {calendarViewMode !== CalendarViewType.WeekView && (
            <PopoverButton
              type="button"
              data-test-id="calendar-select-week-view"
              id="calendar-select-week-view"
              key="calendar-select-week-view"
              aria-label="calendar-select-week-view"
              className="flex min-w-full cursor-pointer flex-row gap-1 p-1 py-3 hover:bg-slate-500 hover:text-white"
              onClick={() => {
                dispatch(setCalendarViewMode(CalendarViewType.WeekView));
              }}
            >
              <span className="flex flex-col items-center justify-center rounded-full p-1">
                <MdOutlineViewWeek size={18} />
              </span>
              <span className="flex grow flex-col items-start justify-center text-start text-base font-bold">
                {t(
                  'static.dashboard.appointments_section.week_view_popup_label',
                )}
              </span>
            </PopoverButton>
          )}

          {calendarViewMode !== CalendarViewType.DayView && (
            <PopoverButton
              type="button"
              data-test-id="calendar-select-day-view"
              id="calendar-select-day-view"
              key="calendar-select-day-view"
              aria-label="calendar-select-day-view"
              className="flex min-w-full cursor-pointer flex-row gap-1 p-1 py-3 hover:bg-slate-500 hover:text-white"
              onClick={() => {
                dispatch(setCalendarViewMode(CalendarViewType.DayView));
              }}
            >
              <span className="flex flex-col items-center justify-center rounded-full p-1">
                <MdOutlineCalendarViewDay size={18} />
              </span>
              <span className="flex grow flex-col items-start justify-center text-start text-base font-bold">
                {t(
                  'static.dashboard.appointments_section.day_view_popup_label',
                )}
              </span>
            </PopoverButton>
          )}

          {calendarViewMode !== CalendarViewType.ListView && (
            <PopoverButton
              type="button"
              data-test-id="calendar-select-month-view"
              id="calendar-select-month-view"
              key="calendar-select-month-view"
              aria-label="calendar-select-month-view"
              className="flex min-w-full cursor-pointer flex-row gap-1 p-1 py-3 hover:bg-slate-500 hover:text-white"
              onClick={() => {
                dispatch(setCalendarViewMode(CalendarViewType.ListView));
              }}
            >
              <span className="flex flex-col items-center justify-center rounded-full p-1">
                <BsViewList size={18} />
              </span>
              <span className="flex grow flex-col items-start justify-center text-start text-base font-bold">
                {t(
                  'static.dashboard.appointments_section.list_view_popup_label',
                )}
              </span>
            </PopoverButton>
          )}
        </PopoverPanel>
      </Popover>
    </span>
  );
};

export default CalendarPopupMenu;
